import { useState, useCallback, useRef } from 'react'
import { DragEndEvent, DragStartEvent, DragMoveEvent, UniqueIdentifier } from '@dnd-kit/core'

interface Position {
  x: number
  y: number
}

interface WorkspaceElement {
  id: string
  type: string
  position: Position
  size: { width: number; height: number }
  data: any
  groupId?: string
}

interface UseDraggableWorkspaceProps {
  workspaceElements: WorkspaceElement[]
  selectedElements: string[]
  onElementDrag: (elementId: string, newPosition: Position) => void
  onDragStart?: (elementId: string) => void
  onDragEnd?: (updates: { elementId: string, finalPosition: Position }[]) => void
  screenDeltaToSVGDelta: (deltaX: number, deltaY: number) => Position
  elementGroups?: Record<string, { name: string, elementIds: string[], permanent?: boolean }>
  snapPosition?: (elementId: string, position: Position) => Position
  readOnly?: boolean
}

export function useDraggableWorkspace({
  workspaceElements,
  selectedElements,
  onElementDrag,
  onDragStart,
  onDragEnd,
  screenDeltaToSVGDelta,
  elementGroups = {},
  snapPosition,
  readOnly = false
}: UseDraggableWorkspaceProps) {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null)
  const [currentDelta, setCurrentDelta] = useState<Position>({ x: 0, y: 0 })
  const initialPositions = useRef<Map<string, Position>>(new Map())
  const draggedElements = useRef<string[]>([])

  const handleDragStart = useCallback((event: DragStartEvent) => {
    if (readOnly) return
    const elementId = event.active.id as string
    setActiveId(elementId)
    setCurrentDelta({ x: 0, y: 0 })

    // Vérifier si l'élément fait partie d'un groupe permanent
    const draggedElement = workspaceElements.find(el => el.id === elementId)
    const groupId = draggedElement?.groupId
    const group = groupId ? elementGroups[groupId] : null
    
    let elementsToMove: string[]
    
    // Si l'élément fait partie d'un groupe permanent, déplacer tout le groupe
    if (group?.permanent) {
      elementsToMove = group.elementIds
    }
    // Sinon, utiliser la logique de sélection existante
    else if (selectedElements.length > 0 && selectedElements.includes(elementId)) {
      elementsToMove = selectedElements
    } else {
      elementsToMove = [elementId]
    }

    draggedElements.current = elementsToMove

    const positions = new Map<string, Position>()
    elementsToMove.forEach(id => {
      const element = workspaceElements.find(el => el.id === id)
      if (element) {
        positions.set(id, { ...element.position })
      }
    })
    initialPositions.current = positions

    onDragStart?.(elementId)
  }, [workspaceElements, selectedElements, onDragStart, elementGroups])

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    if (!event.delta || !activeId) return

    const svgDelta = screenDeltaToSVGDelta(event.delta.x, event.delta.y)

    if (snapPosition) {
      const primaryId = activeId as string
      const initialPos = initialPositions.current.get(primaryId)
      if (initialPos) {
        const rawPosition = { x: initialPos.x + svgDelta.x, y: initialPos.y + svgDelta.y }
        const snapped = snapPosition(primaryId, rawPosition)
        const snappedDelta = { x: snapped.x - initialPos.x, y: snapped.y - initialPos.y }
        setCurrentDelta(snappedDelta)
        return
      }
    }

    setCurrentDelta(svgDelta)
  }, [activeId, screenDeltaToSVGDelta, snapPosition])

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    if (!event.delta || !activeId) {
      setActiveId(null)
      setCurrentDelta({ x: 0, y: 0 })
      initialPositions.current = new Map()
      draggedElements.current = []
      return
    }

    const svgDelta = screenDeltaToSVGDelta(event.delta.x, event.delta.y)

    // Calculer le delta snappé à partir de l'élément principal
    let snappedDeltaX = svgDelta.x
    let snappedDeltaY = svgDelta.y
    if (snapPosition) {
      const primaryId = activeId as string
      const initialPos = initialPositions.current.get(primaryId)
      if (initialPos) {
        const rawPosition = { x: initialPos.x + svgDelta.x, y: initialPos.y + svgDelta.y }
        const snapped = snapPosition(primaryId, rawPosition)
        snappedDeltaX = snapped.x - initialPos.x
        snappedDeltaY = snapped.y - initialPos.y
      }
    }
    
    // Calculer toutes les nouvelles positions en une seule fois
    const updates = draggedElements.current
      .map(elementId => {
        const initialPos = initialPositions.current.get(elementId)
        if (initialPos) {
          return {
            elementId,
            finalPosition: {
              x: initialPos.x + snappedDeltaX,
              y: initialPos.y + snappedDeltaY
            }
          }
        }
        return null
      })
      .filter((update): update is { elementId: string, finalPosition: Position } => update !== null)
    
    // Mettre à jour toutes les positions en une seule fois
    if (updates.length > 0) {
      onDragEnd?.(updates)
    }
    
    setActiveId(null)
    setCurrentDelta({ x: 0, y: 0 })
    initialPositions.current = new Map()
    draggedElements.current = []
  }, [activeId, onDragEnd, screenDeltaToSVGDelta, snapPosition])

  const handleDragCancel = useCallback(() => {
    setActiveId(null)
    setCurrentDelta({ x: 0, y: 0 })
    initialPositions.current = new Map()
    draggedElements.current = []
  }, [])

  return {
    activeId,
    currentDelta,
    draggedElements: draggedElements.current,
    handleDragStart,
    handleDragMove,
    handleDragEnd,
    handleDragCancel
  }
}

